<template>
  <v-form :disabled="project.readonly">
    <v-card>
      <v-card-title>
        <h3>{{ project.title }} アスベスト事前調査詳細表</h3>
      </v-card-title>

      <v-card-text style="overflow: scroll">
        <div class="row ma-3">
          全{{ project.rooms.length }}部屋中、
          {{ currentPage * perPageCount + 1 }}部屋目から{{ (currentPage + 1) * perPageCount }}部屋目までを表示。
          <div>
            <v-chip
              v-for="(page, pIndex) in pageNames"
              :key="pIndex"
              :color="parseInt(pIndex, 10) === currentPage ? 'primary' : 'default'"
              small
              @click.prevent="onPageChanged(parseInt(pIndex, 10))">
              {{ parseInt(pIndex, 10) + 1 }}ページ
              ({{ page[0] }}<span v-if="page.length > 1">〜{{ page[page.length - 1] }}</span>)
            </v-chip>
          </div>
        </div>
        <div class="row">
          <span class="col-3">
            <v-select
              v-if="false"
              v-model="filterRoomIds"
              :items="selectableRooms"
              multiple
              label="調査部屋絞り込み"
              dense
              outlined
              @change="onRoomIdsChanged">
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0">
                  <span>{{ item.text }}</span>
                </v-chip>
                <span
                  v-if="index === 1"
                  class="grey--text caption">
                  (その他 {{ filterRoomIds.length - 1 }} 件)
                </span>
              </template>
            </v-select>
          </span>
          <span class="col-2">
            <v-checkbox
              v-model="showWrittenSurvey"
              label="書面調査結果欄を表示"
              dense />
          </span>
          <span class="col-2">
            <v-checkbox
              v-model="showFieldSurvey"
              label="現地調査結果欄を表示"
              dense />
          </span>
          <span class="col-2">
            <v-checkbox
              v-model="showAnalysis"
              label="診断欄を表示"
              dense />
          </span>
        </div>
        <div
          v-if="loading"
          class="ml-auto mr-auto mt-10 text-center">
          <v-progress-circular
            indeterminate>
          </v-progress-circular>
        </div>
        <table
          v-else
          class="detail-table">
          <tr>
            <td>
              調査部屋No
            </td>
            <td>
              階数
            </td>
            <td>
              部屋名<br />（竣工年・改修年）
            </td>
            <td><template v-if="showWrittenSurvey">書面調査結果</template></td>
            <td v-if="showFieldSurvey">現地調査</td>
            <td v-if="showAnalysis">診断</td>
          </tr>
          <tr
            v-for="room in filteredRooms"
            :key="room.id">
            <td :class="asbestosRoomClassName(room)">
              {{ room.room_no }}
            </td>
            <td :class="asbestosRoomClassName(room)">
              {{ floorName(room.field_floors) }}階
            </td>
            <td :class="asbestosRoomClassName(room)">
              {{ room.field_room_name }}
            </td>
            <td>
              <table
                v-if="hasSurveys(room)"
                class="survey-table">
                <tr>
                  <th>①部位</th>
                  <th v-if="showWrittenSurvey">②図面の有無</th>
                  <th v-if="showWrittenSurvey">③建材名・商品名</th>
                  <th v-if="showWrittenSurvey">④石綿含有の可能性</th>
                  <th v-if="showWrittenSurvey">⑤石綿有無の判断根拠</th>
                </tr>
                <tbody
                  v-for="(part, pIndex) in room.parts"
                  :key="pIndex">
                  <template
                    v-if="part.surveys.length > 0">
                    <tr
                      v-for="(survey, sIndex) in part.surveys"
                      :key="sIndex">
                      <td
                        v-if="sIndex === 0"
                        :rowspan="part.surveys.length"
                        :class="asbestosPartClassName(part)"
                        style="width: 120px"
                        :style="
                          {height:`${(part.surveys.length * 33)+(part.surveys.length - 1)}px`}
                        ">
                        {{ part.name }}
                      </td>
                      <td
                        v-else-if="!showWrittenSurvey"
                        style="width:0;padding:0;margin:0;">
                      </td>
                      <td
                        v-if="showWrittenSurvey"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.has_drawings"
                          :select-items="drawings"
                          item-text="value"
                          item-value="id"
                          select-width="120" />
                      </td>
                      <td
                        v-if="showWrittenSurvey"
                        :class="asbestosSurveyClassName(room, survey)">
                        <edit-dialog
                          v-model="survey.written_material"
                          style="min-width: 230px">
                          <v-text-field
                            v-model="survey.written_material"
                            :disabled="fieldDisabled(survey)"
                            class="mt-5"
                            width="150px"
                            label="建材名・商品名"
                            counter
                            autofocus
                            outlined
                            dense />
                        </edit-dialog>
                      </td>
                      <td
                        v-if="showWrittenSurvey"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.written_status_id"
                          :select-items="statuses"
                          item-text="value"
                          item-value="id" />
                      </td>
                      <td
                        v-if="showWrittenSurvey"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.written_reason_id"
                          :select-items="reasons"
                          item-text="value"
                          item-value="id"
                          select-width="250" />
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              <div
                v-else-if="showWrittenSurvey"
                class="no-data-row">
                調査結果が入力されていません
              </div>
            </td>
            <td
              v-if="showFieldSurvey"
              :style="{ 'background-color': !!room.disable_field_survey ? 'lightgray' : '' }">
              <div
                v-if="!!room.disable_field_survey"
                class="no-data-row">
                この部屋は無いため現地調査結果報告なし。
              </div>
              <table
                v-else-if="hasSurveys(room)"
                class="survey-table"
                style="width: 100%">
                <tr>
                  <th>1) 書面調査との<br />整合性確認</th>
                  <th>2) 建材名・商品名</th>
                  <th>3) 現地調査での確認内容及びAS/VS状況他</th>
                  <th>4) 実施VS</th>
                  <th>5) ｻﾝﾌﾟﾘﾝｸﾞ№</th>
                  <th>6) 代表サンプルor同等品</th>
                </tr>
                <tbody
                  v-for="(part, pIndex) in room.parts"
                  :key="pIndex">
                  <template
                    v-if="part.surveys.length > 0">
                    <tr
                      v-for="(survey, sIndex) in part.surveys"
                      :key="sIndex">
                      <td :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.is_consistent"
                          :select-items="consistents"
                          item-text="value"
                          item-value="id"
                          :select-width="80" />
                      </td>
                      <td
                        :class="asbestosSurveyClassName(room, survey)"
                        :style="{
                          'background-color': fieldDisabled(survey)? 'lightgray' : 'white'
                        }">
                        <edit-dialog
                          v-model="survey.field_material"
                          style="min-width: 200px">
                          <v-text-field
                            v-model="survey.field_material"
                            :disabled="fieldDisabled(survey)"
                            class="mt-5"
                            width="150px"
                            label="建材名"
                            counter
                            autofocus
                            outlined
                            dense />
                          <v-checkbox
                            label="無し"
                            :value="fieldDisabled(survey)"
                            @change="(val) => {
                              onDisableFieldClicked(val, room, part, sIndex)
                            }" />
                        </edit-dialog>
                      </td>
                      <td
                        v-if="fieldDisabled(survey)"
                        colspan="4"
                        style="background-color: lightgray">
                        現地調査時この部位はなしと確認。
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <edit-dialog
                          v-model="survey.field_remarks"
                          style="min-width: 530px" />
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-if="!fieldDisabled(survey)"
                          v-model="survey.field_vs_no"
                          :select-items="fieldVsItems"
                          item-text="text"
                          item-value="value"
                          :select-width="120"
                          :suffix="fieldVsNo(survey)" />
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <edit-dialog
                          v-if="!fieldDisabled(survey)"
                          v-model="survey.field_sampling"
                          style="min-width: 130px" />
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.sampling_result"
                          :select-items="samplingResults"
                          item-text="value"
                          item-value="id"
                          :select-width="100" />
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              <div
                v-else
                class="no-data-row">
                調査結果が入力されていません
              </div>
            </td>
            <td
              v-if="showAnalysis"
              :style="{ 'background-color': !!room.disable_field_survey ? 'lightgray' : '' }">
              <div
                v-if="!!room.disable_field_survey"
                class="no-data-row">
                この部屋は無いため現地調査結果報告なし。
              </div>
              <table
                v-else-if="hasSurveys(room)"
                class="survey-table">
                <tr>
                  <th>7) 判断根拠</th>
                  <th>8) 添付資料</th>
                  <th>9) 石綿の有無</th>
                  <th>10) 石綿の種類</th>
                  <th>11) 材料ﾚﾍﾞﾙ</th>
                  <th>12) グループワード</th>
                </tr>
                <tbody
                  v-for="(part, pIndex) in room.parts"
                  :key="pIndex">
                  <template
                    v-if="part.surveys.length > 0">
                    <tr
                      v-for="(survey, sIndex) in part.surveys"
                      :key="sIndex">
                      <td
                        v-if="fieldDisabled(survey)"
                        colspan="5"
                        style="background-color: lightgray; height: 33px">
                        現地調査時この部位はなしと確認。
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.field_reason_id"
                          :select-items="reasons"
                          item-text="value"
                          item-value="id">
                        </select-dialog>
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <edit-dialog
                          v-model="survey.documents"
                          style="min-width: 230px" />
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.has_asbestos"
                          :select-items="hasAsbestosTypes"
                          item-text="value"
                          item-value="id"
                          style="text-align: center"
                          :select-width="100"
                          @change="onHasAsbestosChanged(survey)" />
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.asbestos_type"
                          :select-items="asbestosTypes"
                          item-text="alias"
                          item-value="id"
                          multiple
                          :select-width="220"
                          style="min-width: 230px"
                          @change="onAsbestosTypeChanged(survey)" />
                      </td>
                      <td
                        v-if="!fieldDisabled(survey)"
                        :class="asbestosSurveyClassName(room, survey)">
                        <select-dialog
                          v-model="survey.asbestos_level"
                          :select-items="asbestosLevels"
                          item-text="value"
                          item-value="id"
                          :disabled="fieldDisabled(survey)"
                          :select-width="140" />
                      </td>
                      <td :class="asbestosSurveyClassName(room, survey)">
                        <edit-dialog
                          style="min-width: 230px">
                          <template #text>
                            <span class="black--text">
                              {{ groupingStatus(survey) }}
                            </span>
                          </template>
                          <v-combobox
                            v-model="survey.field_group_word1"
                            :items="groupWord1Options"
                            class="mt-5"
                            width="150px"
                            label="グループワード1"
                            counter
                            autofocus
                            outlined
                            dense />
                          <v-combobox
                            v-model="survey.field_group_word2"
                            :items="groupWord2Options"
                            class="mt-5"
                            width="150px"
                            label="グループワード2"
                            counter
                            autofocus
                            outlined
                            dense />
                          <v-combobox
                            v-model="survey.field_group_word3"
                            :items="groupWord3Options"
                            class="mt-5"
                            width="150px"
                            label="グループワード3"
                            counter
                            autofocus
                            outlined
                            dense />
                        </edit-dialog>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              <div
                v-else
                class="no-data-row">
                調査結果が入力されていません
              </div>
            </td>
          </tr>
        </table>
      </v-card-text>
    </v-card>
    <global-footer>
      <template #left>
        <v-btn
          large
          @click.prevent="onReturnClicked">
          <v-icon>mdi-chevron-left</v-icon>
          戻る
        </v-btn>
      </template>
      <template #right>
        <v-btn
          v-if="!project.readonly"
          large
          color="default"
          class="mr-1"
          :disabled="!hasChanges"
          @click.prevent="onDraftSaveClicked">
          ブラウザに一時保存
        </v-btn>
        <v-btn
          v-if="!project.readonly"
          large
          color="primary"
          @click.prevent="onNextClicked">
          次へ
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          large
          color="warning"
          @click.prevent="onFinishClicked">
          修了
          <v-icon right>mdi-exit-run</v-icon>
        </v-btn>
      </template>
    </global-footer>
  </v-form>
</template>

<script>
import {
  getStepSession, markAsUnsavedSession, setStepSession,
} from '../../logic/session'
import SelectDialog from '../SelectDialog'
import EditDialog from '../EditDialog'

import { TYPE_MAYBE } from '../../consts/HasAsbestoType'

export default {
  name: 'DetailFormStep1',
  components: {
    EditDialog,
    SelectDialog,
  },
  props: {
    statuses: {
      type: Array,
      default: () => ([]),
    },
    reasons: {
      type: Array,
      default: () => ([]),
    },
    samplings: {
      type: Array,
      default: () => ([]),
    },
    results: {
      type: Array,
      default: () => ([]),
    },
    samplingResults: {
      type: Array,
      default: () => ([]),
    },
    asbestosTypes: {
      type: Array,
      default: () => ([]),
    },
    hasAsbestosTypes: {
      type: Array,
      default: () => ([]),
    },
    drawings: {
      type: Array,
      default: () => ([
        { id: null, value: '不明' },
        { id: 1, value: '有' },
        { id: 0, value: '無' },
      ]),
    },
    consistents: {
      type: Array,
      default: () => ([
        { id: null, value: '' },
        { id: 1, value: '○' },
        { id: 0, value: '×' },
      ]),
    },
    asbestosLevels: {
      type: Array,
      default: () => ([
        { id: null, value: '' },
        { id: 1, value: 'レベル1' },
        { id: 2, value: 'レベル2' },
        { id: 3, value: 'レベル3' },
        { id: 4, value: 'レベル3 (高含有)' },
        { id: 5, value: '目視不可' },
      ]),
    },
    fieldVsItems: {
      type: Array,
      default: () => ([
        { text: '', value: null },
        { text: '有り', value: 'VS' },
        { text: '無し', value: '' },
      ]),
    },
  },
  data() {
    return {
      tab: null,
      project: {
        title: '',
        rooms: [
        ],
      },
      loading: false,
      selectedRooms: [],
      copyRoomDialogIsShown: false,
      deletionPartIndex: null,
      dialog: false,
      tabIndex: 0,
      writtenFieldId: 0,
      filterRoomIds: [],
      selectableRooms: [],
      showWrittenSurvey: true,
      showFieldSurvey: true,
      showAnalysis: true,
      error: {},
      hasChanges: false,
      currentPage: 0,
      perPageCount: 5,
    }
  },
  computed: {
    currentPartNames() {
      if (!this.project.rooms[this.tabIndex]) {
        return []
      }
      return this.project.rooms[this.tabIndex].parts.map(part => part.name)
    },
    filteredRooms() {
      return this.project.rooms.slice(
        this.currentPage * this.perPageCount,
        (this.currentPage + 1) * this.perPageCount,
      )
      // return this.project.rooms.filter(
      //     room => window._.includes(this.filterRoomIds, room.id),
      //   ).slice(this.currentPage, this.perPageCount)
    },
    totalPage() {
      return Math.round(this.project.rooms.length / this.perPageCount)
    },
    pageNames() {
      const pages = {}
      this.project.rooms.forEach((room, index) => {
        const pageIndex = Math.floor(index / this.perPageCount)
        if (!pages[pageIndex]) {
          pages[pageIndex] = []
        }
        pages[pageIndex].push(room.room_no)
      })
      return pages
    },
    deemedInclusionGroupOptions() {
      return this.project.rooms.reduce((total, room) => [
        ...total,
        ...room.parts.reduce((partsTotal, part) => [
          ...partsTotal,
          ...part.surveys.reduce((surveysTotal, survey) => {
            if (survey.maybe_asbesto_group === null) {
              return surveysTotal
            }

            return [
              ...surveysTotal,
              survey.maybe_asbesto_group,
            ]
          }, []),
        ], []),
      ], []).filter(item => item)
    },
    groupWord1Options() {
      return this.project.rooms.reduce((total, room) => [
        ...total,
        ...room.parts.reduce((partsTotal, part) => [
          ...partsTotal,
          ...part.surveys.reduce((surveysTotal, survey) => {
            if (survey.field_group_word1 === null) {
              return surveysTotal
            }

            return [
              ...surveysTotal,
              survey.field_group_word1,
            ]
          }, []),
        ], []),
      ], []).filter(item => item)
    },
    groupWord2Options() {
      return this.project.rooms.reduce((total, room) => [
        ...total,
        ...room.parts.reduce((partsTotal, part) => [
          ...partsTotal,
          ...part.surveys.reduce((surveysTotal, survey) => {
            if (survey.field_group_word2 === null) {
              return surveysTotal
            }

            return [
              ...surveysTotal,
              survey.field_group_word2,
            ]
          }, []),
        ], []),
      ], []).filter(item => item)
    },
    groupWord3Options() {
      return this.project.rooms.reduce((total, room) => [
        ...total,
        ...room.parts.reduce((partsTotal, part) => [
          ...partsTotal,
          ...part.surveys.reduce((surveysTotal, survey) => {
            if (survey.field_group_word3 === null) {
              return surveysTotal
            }

            return [
              ...surveysTotal,
              survey.field_group_word3,
            ]
          }, []),
        ], []),
      ], []).filter(item => item)
    },
    groupingStatus() {
      return (survey) => ([
        survey.field_group_word1,
        survey.field_group_word2,
        survey.field_group_word3,
      ].filter(item => item).length > 0 ? 'グループ設定あり' : 'グループ設定なし')
    },
  },
  watch: {
    hasChanges() {
      this.$emit('hasChange', this.hasChanges)
    },
  },
  mounted() {
    this.loadItem()
  },
  methods: {
    isSelectedMaygeAsbesto(item) {
      return item.has_asbestos === TYPE_MAYBE
    },
    loadItem() {
      const project = getStepSession('detail', 1)
      if (project) {
        this.project = project
      }
      this.$watch('project', () => {
        this.hasChanges = true
      }, { deep: true })
      this.selectableRooms = [
        { value: null, text: '全て' },
        ...this.project.rooms.map(room => ({
          value: room.id,
          text: `[${room.room_no}] ${room.room_name}`,
        })),
      ]
      // this.filterRoomIds = this.project.rooms ? [this.project.rooms[0].id] : []
      this.filterRoomIds = this.project.rooms.slice(this.currentPage, this.perPageCount)
    },
    async onPageChanged(page) {
      this.loading = true
      this.currentPage = page
      this.$nextTick(() => {
        setTimeout(() => {
          this.loading = false
        }, 500)
      })
    },
    async validateItem() {
      try {
        const { rooms } = this.project
        await window.axios.post('/api/details/step2', { rooms })
        this.error = {}
        return true
      } catch (e) {
        if (e.response.status === 422) {
          this.error = e.response.data.errors
          const [errorKey] = Object.keys(this.error)
          const [, roomIndex] = errorKey.split('.')
          this.tabIndex = roomIndex
        }
      }
      return false
    },
    onReturnClicked() {
      this.$emit('change', 1)
    },
    async onNextClicked() {
      if (await this.validateItem()) {
        if (this.hasChanges) {
          markAsUnsavedSession('detail')
        }
        setStepSession('detail', 1, this.project)
        setStepSession('detail', 2, this.project.id)
        this.$emit('change', 3)
      }
    },
    onFinishClicked() {
      this.$router.push({ name: 'index' })
    },
    updateValue(...args) {
      const [newVal, index] = args
      this.defaultRoomParts.splice(index, 1, newVal)
    },
    hasSurveys(room) {
      return room.parts
        .map(part => part.surveys.length)
        .reduce((prev, current) => prev + current) > 0
    },
    asbestosRoomClassName(room) {
      if (room.parts.filter(
        part => part.surveys.filter(survey => !!survey.has_asbestos).length,
      ).length === 0) {
        return {}
      }

      let minLevel = 99
      room.parts.filter(part => part.surveys.length).map(part => part.surveys.forEach(survey => {
        if (survey.has_asbestos && survey.asbestos_level && survey.asbestos_level < minLevel) {
          minLevel = survey.asbestos_level
        }
      }))

      const css = {}
      css[`col-level-${minLevel}`] = true

      return css
    },
    asbestosPartClassName(part) {
      let minLevel = 99
      if (!part.surveys.filter(survey => !!survey.has_asbestos).length) {
        return {}
      }
      part.surveys.forEach(survey => {
        if (survey.has_asbestos && survey.asbestos_level && survey.asbestos_level < minLevel) {
          minLevel = survey.asbestos_level
        }
      })

      const css = {}
      css[`col-level-${minLevel}`] = true

      return css
    },
    asbestosSurveyClassName(room, survey) {
      if (!survey.has_asbestos) {
        return {}
      }
      const css = {}
      css[`col-level-${survey.asbestos_level ?? 99}`] = true

      return css
    },
    onHasAsbestosChanged(survey) {
      if (survey.has_asbestos === -1) {
        // eslint-disable-next-line no-param-reassign
        survey.asbestos_type = [-1]
        // eslint-disable-next-line no-param-reassign
        survey.asbestos_level = this.asbestosLevels.find((level) => level.value === '目視不可').id ?? null
      }
    },
    onAsbestosTypeChanged(survey) {
      if (survey.asbestos_type.filter(item => item === -1).length > 0) {
        // eslint-disable-next-line no-param-reassign
        survey.asbestos_type = [-1]
      }
    },
    onDraftSaveClicked() {
      setStepSession('detail', 1, this.project)
      markAsUnsavedSession('detail')
      this.hasChanges = false
    },
    floorName(floor) {
      if (floor === 99) {
        return 'RF'
      }
      return floor > 0
        ? `${floor}`
        : `地下${Math.abs(floor)}`
    },
    onRoomIdsChanged() {
      if (this.filterRoomIds.length === 0) {
        this.filterRoomIds = [null]
        return
      }
      if (this.filterRoomIds.filter((room) => room === null).length === 1) {
        if (this.filterRoomIds.length === this.project.rooms.length + 1) {
          this.filterRoomIds = []
        } else {
          this.filterRoomIds = this.project.rooms.map(room => room.id)
        }
      }
    },
    fieldDisabled(survey) {
      return survey.field_material === '無し'
    },
    onDisableFieldClicked(disabled, room, part, surveyIndex) {
      const survey = part.surveys[surveyIndex]
      survey.field_material = disabled ? '無し' : ''
      const roomIndex = this.project.rooms.indexOf(room)
      const partIndex = room.parts.indexOf(part)
      if (disabled) {
        survey.field_information = ''
        survey.field_vs_no = ''
        survey.field_sampling = ''
        survey.sampling_result = ''
        survey.field_reason_id = ''
        survey.documents = ''
        survey.has_asbestos = ''
        survey.asbestos_type = ''
        survey.asbestos_level = ''
        this.$set(
          this.project.rooms[roomIndex].parts[partIndex].surveys[surveyIndex],
          survey,
        )
      }
    },
    fieldVsNo(currentSurvey) {
      let vsNo = 0
      let currentVs = ''
      this.project.rooms.forEach(room => {
        room.parts.forEach(part => {
          part.surveys.forEach(survey => {
            if (survey.field_vs_no === 'VS') {
              vsNo += 1
              if (currentSurvey.id === survey.id) {
                currentVs = `VS${vsNo}`
              }
            }
          })
        })
      })
      return currentVs
    },
  },
}
</script>

<style scoped>
.detail-table {
  border-collapse:separate;
  border-spacing: 1px;
  background-color: #a0a0a0;
}
.detail-table TD {
  background-color: #ffffff;
  padding: 1px;
  white-space: nowrap;
  vertical-align: top;
}
.survey-table {
  margin-top: 0;
  margin-bottom: 0;
}
.survey-table TD {
  border-bottom: 1px solid #d9d9d9;
  border-right: 1px solid #d9d9d9;
}
.survey-table TH{
  border-bottom: 1px solid #d9d9d9;
  border-right: 1px solid #d9d9d9;
  height: 45px;
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
}
.survey-table TD:last-child, .survey-table TH:last-child {
  border-right: none;
}
.col-level-1 {
  background-color: #E57373!important;
  color: #000000;
}
.col-level-2 {
  background-color: #FFF59D!important;
  color: #000000;
}
.col-level-3 {
  background-color: #66BB6A!important;
  color: #000000;
}
.col-level-4 {
  background-color: #64B5F6!important;
  color: #000000;
}
.col-level-5 {
  background-color: #ffa636!important;
  color: #000000;
}
.col-level-99 {
  background-color: #64B5F6!important;
  color: #000000;
}
.no-data-row {
  padding: 20px;
}
</style>
